import { SettingsIcon } from "@/components/Icons/SettingsIcon";
import { Badge } from "@/components/ui/badge";
import { Loader } from "@/components/Common/Loader/Loader";
import { cn } from "@/lib/utils";

import { containerVerticalFlowAnalytics } from "../analytics";
import { recipeDisplayTitle } from "./RecipesSection.constants";
import { RecipeLogo } from "./RecipeLogo";
import { useRecipesSection } from "../hooks/useRecipesSection";

type RecipesFirstStepProps = {
  onSelectGpu: () => void;
  /** After selecting a recipe, advance to the processor step (same as Custom Deployment). */
  onAdvanceRecipesStep?: () => void;
};

const CUSTOM_DEPLOYMENT_TILE = {
  title: "Custom Deployment",
  description:
    "Skip the recipes. Provide your own Docker image, environment variables, and custom configurations for full control."
} as const;

export const RecipesFirstStep: React.FC<RecipesFirstStepProps> = ({
  onSelectGpu,
  onAdvanceRecipesStep
}) => {
  const { values, allProcessedRecipes, isHardwareInventoryLoading, onSelect } = useRecipesSection();
  const recipesToRender = allProcessedRecipes;

  const selectRecipeAndAdvance = (recipeKey: string) => {
    if (values.containerRecipe !== recipeKey) {
      containerVerticalFlowAnalytics.trackRecipesFirstRecipeSelected({ recipe: recipeKey });
    }
    onSelect(recipeKey);
    window.setTimeout(() => {
      onAdvanceRecipesStep?.();
    }, 0);
  };

  if (isHardwareInventoryLoading) {
    return (
      <div
        className="flex min-h-[240px] flex-col items-center justify-center gap-3 px-6"
        aria-busy="true"
        aria-live="polite"
      >
        <Loader className="h-[40px] w-[40px]" />
        <p className="subtext-small text-muted-foreground">Preparing recipes…</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 px-6">
      <div className="flex flex-wrap gap-4 rounded-lg border border-gray-dark-200 bg-muted/30 p-4">
        {recipesToRender.map((recipe) => (
          <button
            type="button"
            key={recipe.key}
            className={cn(
              "flex min-w-[min(100%,280px)] flex-1 cursor-pointer flex-col justify-between rounded-lg border bg-background p-5 text-left shadow-sm outline-none transition-[border-color,box-shadow,background-color] hover:border-primary/35 hover:shadow-md focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
              values.containerRecipe === recipe.key
                ? "border-primary bg-accent shadow-md ring-1 ring-primary/15"
                : "border-gray-dark-200",
              (recipe.isSoldOut || !recipe.isCompatible) &&
                "pointer-events-none cursor-not-allowed bg-muted/40 opacity-60 hover:border-gray-dark-200 hover:shadow-sm"
            )}
            onClick={() =>
              !(recipe.isSoldOut || !recipe.isCompatible) && selectRecipeAndAdvance(recipe.key)
            }
          >
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <RecipeLogo logoUrl={recipe.logoUrl} alt={recipeDisplayTitle(recipe)} />
                <div className="subtext-medium line-clamp-2 min-w-0 flex-1 font-semibold leading-6 tracking-tight">
                  {recipeDisplayTitle(recipe)}
                </div>
              </div>
              {recipe.tags && recipe.tags.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {recipe.tags.map((tag, idx) => (
                    <Badge
                      key={`${recipe.key}-${idx}`}
                      variant="subtle"
                      className="rounded-lg font-normal"
                    >
                      {tag}
                    </Badge>
                  ))}
                </div>
              ) : null}
              {!recipe.isCompatible ? (
                <div className="text-xs font-semibold text-muted-foreground">
                  {recipe.availabilityLabel}
                </div>
              ) : recipe.isSoldOut ? (
                <div className="text-xs font-semibold text-muted-foreground">Hardware Sold Out</div>
              ) : null}
              <div className="subtext-small line-clamp-3 leading-[20px] text-muted-foreground">
                {recipe.description}
              </div>
            </div>
          </button>
        ))}
        <button
          type="button"
          className="flex min-w-[min(100%,280px)] flex-1 cursor-pointer flex-col justify-between rounded-lg border border-dashed border-gray-dark-300 bg-background p-5 text-left shadow-sm outline-none transition-[border-color,box-shadow,background-color] hover:border-primary/40 hover:bg-muted/20 hover:shadow-md focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          onClick={onSelectGpu}
        >
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <div
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-dashed border-gray-dark-300 bg-muted/30 text-muted-foreground"
                aria-hidden
              >
                <SettingsIcon className="h-5 w-5" />
              </div>
              <div className="subtext-medium line-clamp-2 min-w-0 flex-1 font-semibold leading-6 tracking-tight">
                {CUSTOM_DEPLOYMENT_TILE.title}
              </div>
            </div>
            <div className="subtext-small line-clamp-4 leading-[20px] text-muted-foreground">
              {CUSTOM_DEPLOYMENT_TILE.description}
            </div>
          </div>
        </button>
      </div>
    </div>
  );
};
